import * as React from 'react';
import axios from 'axios';
import {
  useRecurly, CardNumberElement, CardMonthElement, CardYearElement, CardCvvElement,
} from '@recurly/react-recurly';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { StepShell, Card, Button, Alert } from '../../components/ui';
import Select from '../../components/select.component';
import LookingFor from '../../components/looking-for';
import useFieldLift from '../../hooks/use-field-lift';
import { billingFailureText, isBillingAddressFailure } from '../../utils/billing-error';
import { maskPostal, restoreZipHyphenLeadingZero, padZipLeadingZero } from '../../utils/postal';
import useWallets from '../../hooks/use-wallets';
import useFit from '../../hooks/use-fit';
import useQuote from '../../hooks/use-quote';
import { reportPayError } from '../../utils/pay-errors';
import { SHIPPING_PRICE, estimatedTax } from '../../utils/quote';
import {
  PROMO_PRICES, DEFAULT_RENEWAL_PRICE, BONUS_TITLES, BONUS_VALUES, COMMERCE, countryOptions, cadenceFromPlanCode,
} from '../../config';
// Real assets pulled from the design project itself (claude-design MCP) —
// each brand needs two distinct marks: a plain colored one for the row, a
// "classic" one for its branded CTA — except PayPal, whose row and CTA share
// the same classic mark in the design itself, and Google Pay, which never
// gets a CTA mark (its own real button replaces the CTA).
import applePayRowMark from '../../imgs/applepay-mark.svg';
import applePayCtaMark from '../../imgs/applepay-mark-classic.png';
import googlePayRowMark from '../../imgs/google-pay-mark.svg';
import payPalMark from '../../imgs/paypal-mark-classic.png';
import venmoRowMark from '../../imgs/venmo-mark.png';
import venmoCtaMark from '../../imgs/venmo-mark-classic.png';
import cardVisaMark from '../../imgs/card-visa.svg';
import cardMasterMark from '../../imgs/card-master.svg';
import cardAmexMark from '../../imgs/card-amex.svg';
import cardDiscoverMark from '../../imgs/card-discover.svg';
import cardDinersMark from '../../imgs/card-diners.svg';
import cardJcbMark from '../../imgs/card-jcb.svg';
import cardUnionpayMark from '../../imgs/card-unionpay.svg';
import firstBoxIcon from '../../imgs/first-box-icon.png';
import brideSweatshirtPhoto from '../../imgs/bride-sweatshirt.jpg';
import flagCaMark from '../../imgs/flag-ca.svg';

const ELEMENT_STYLE = {
  fontFamily: "Avenir, 'Helvetica Neue', Arial, sans-serif",
  fontSize: '16px',
  fontColor: '#231F1E',
  fontSmoothing: 'antialiased',
  // Our wrapper draws the box, label and icon; the iframe holds only the
  // value text — same division of labor TTM's shared-ui step4-pay.jsx uses.
  placeholder: { content: '' },
  invalid: { fontColor: '#D0494A' },
  input: { padding: '0' },
};

// Card Number's lock icon and the CVV field's "card back with the security
// code highlighted" icon — exact paths/colors read live off the design's own
// mock inputs (STEP 4 OF 4 file), overlaid on top of the Recurly iframe the
// same way TTM's shared-ui does it (an absolutely-positioned, pointer-
// events:none sibling of the iframe, not something baked into Recurly's own
// style config — Recurly's cross-origin iframe can't draw our icon itself).
function CardNumberIcon() {
  return (
    <svg className="ec-cardfield-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="#5F5F5F" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </svg>
  );
}
function CvvIcon() {
  return (
    <svg className="ec-cardfield-icon ec-cvv-icon" width="42" height="28" viewBox="0 0 42 28" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="40" height="26" rx="4" fill="#FFFFFF" stroke="#D7D7D7" strokeWidth="1.5" />
      <rect x="1" y="5" width="40" height="6" fill="#231F1E" />
      <rect x="6" y="15" width="20" height="7" rx="1.5" fill="#F3EFE9" />
      <rect x="25.25" y="14.25" width="12.5" height="8.5" rx="2" fill="#FFFFFF" stroke="#C5292A" strokeWidth="1.5" />
      <g fill="#C5292A">
        <circle cx="28.5" cy="18.5" r="1.1" />
        <circle cx="31.5" cy="18.5" r="1.1" />
        <circle cx="34.5" cy="18.5" r="1.1" />
      </g>
    </svg>
  );
}

// "Please check: X, Y." — the design's own declined/invalid-card banner
// text (verified live via simulate="declined"), built from whichever
// hosted fields Recurly's real token() response names as the problem.
const FIELD_NAMES = {
  number: 'Card Number', month: 'Month', year: 'Year', cvv: 'CVV', first_name: 'Name On Card', last_name: 'Name On Card',
};

// What the customer calls each wallet, keyed by the source useWallets reports.
const WALLET_NAMES = {
  paypal: 'PayPal', applepay: 'Apple Pay', googlepay: 'Google Pay', venmo: 'Venmo',
};

// No per-field inline error TEXT here on purpose: the design never shows
// one under Card Number/Month/Year/CVV (verified live via simulate=
// "declined") — only the one boxed summary banner below the form ("Please
// check: Card Number, CVV."). `invalid` still drives the red border, since
// Recurly's real token() response tells us precisely which hosted field was
// the problem and silently dropping that visual cue would be worse, not
// more faithful.
function HostedField({ label, children, onChange, invalid, icon }) {
  const [focused, setFocused] = React.useState(false);
  const [empty, setEmpty] = React.useState(true);
  const boxRef = React.useRef(null);
  // A plain <input>'s own padding is still part of the clickable element —
  // this box's padding isn't, since the real target is a cross-origin
  // iframe sitting inset inside it. Without this, only that inset iframe
  // rectangle responds to clicks and the rest of the (visually identical)
  // box does nothing, which reads as broken. Clicking anywhere in the box
  // focuses the iframe directly; a real click already inside the iframe
  // passes through to it natively and this is a no-op.
  const focusInner = () => { boxRef.current?.querySelector('iframe')?.focus(); };
  return (
    <div className={`ec-field ec-field--plain ${focused || !empty ? 'has-value' : ''}`.trim()}>
      <div ref={boxRef} onMouseDown={focusInner} className={`ec-field--hosted ${icon ? 'has-icon' : ''} ${focused ? 'is-focus' : ''} ${invalid ? 'is-error' : ''}`.trim()}>
        <div className="ec-field-hosted-el">
          {React.cloneElement(children, {
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            onChange: (s) => { setEmpty(Boolean(s && s.empty)); if (onChange) onChange(s); },
          })}
        </div>
        <span className="ec-field-label">{label}</span>
        {icon}
      </div>
    </div>
  );
}

// Payment-method row — 60px min-height, 12px 16px padding, 22x22 circle that
// goes 1.5px #AFAFAF empty → 6px solid #333939 "filled" when selected, 86x32
// brand-mark slot. Every value read live off the design's own Step 4 render.
// "+4" opens a small dark popover listing the remaining accepted card
// brands — a real, dismissible control in the design (verified live: click
// toggles it, outside pointerdown/Escape closes it), not a static badge.
// stopPropagation/preventDefault on toggle matters here specifically because
// this sits inside the larger "Credit/Debit Card" row button — without it,
// opening the popover would also select the card method underneath it.
const MORE_CARDS = [
  { src: cardDiscoverMark, alt: 'Discover' },
  { src: cardDinersMark, alt: 'Diners Club' },
  { src: cardJcbMark, alt: 'JCB' },
  { src: cardUnionpayMark, alt: 'UnionPay' },
];
function MoreCardsButton() {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === 'keydown' && e.key !== 'Escape') return; setOpen(false); };
    document.addEventListener('pointerdown', close, true);
    document.addEventListener('keydown', close, true);
    return () => {
      document.removeEventListener('pointerdown', close, true);
      document.removeEventListener('keydown', close, true);
    };
  }, [open]);
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); }}
      style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 1 auto',
        alignSelf: 'stretch', width: 28, minWidth: 20, padding: '0 3px', border: '1px solid var(--ec-field-border)', borderRadius: 4,
        background: '#FFFFFF', whiteSpace: 'nowrap', fontSize: 'clamp(9px, 2.4vw, 11px)', fontWeight: 500, color: 'var(--ec-label)', cursor: 'pointer',
      }}
    >
      +4
      {open && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: 'auto', right: 0, zIndex: 20, display: 'flex',
          flexWrap: 'nowrap', alignItems: 'center', gap: 6, padding: 10, borderRadius: 10, background: '#231F1E',
          boxShadow: '0 8px 22px rgba(35, 31, 30, 0.22)',
        }}
        >
          <span style={{
            position: 'absolute', top: '100%', right: 8, width: 10, height: 10, marginTop: -5,
            transform: 'rotate(45deg)', borderRadius: 2, background: '#231F1E',
          }}
          />
          {MORE_CARDS.map((c) => (
            <span key={c.alt} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 28 }}>
              <img src={c.src} alt={c.alt} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: 5 }} />
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

function PayRow({ selected, label, mark, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 60, padding: '12px 16px',
      borderRadius: 10, background: selected ? '#FAFAFA' : '#FFFFFF',
      border: `1.5px solid ${selected ? 'var(--ec-border-open)' : 'var(--ec-border)'}`,
      boxShadow: selected ? 'inset 0 0 0 1px var(--ec-border-open)' : 'none',
      fontFamily: 'var(--ec-font)', color: 'var(--ec-text)', cursor: 'pointer', transition: 'border-color 0.18s, background 0.18s',
    }}
    >
      <span style={{
        flex: '0 0 auto', boxSizing: 'border-box', width: 22, height: 22, borderRadius: 999, transition: '0.18s',
        border: selected ? '6px solid var(--ec-border-open)' : '1.5px solid #AFAFAF', background: '#FFFFFF',
      }}
      />
      <span style={{ flex: '1 1 auto', fontSize: 15, fontWeight: 500, textAlign: 'left' }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 86, height: 32 }}>
        <img src={mark} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
      </span>
    </button>
  );
}

// "↓ Tap "Place order with X" below ↓" — a real clickable shortcut to the CTA
// below, not static text; only shown for a selected non-card method.
function PayHint({ methodName, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
      margin: '-6px 0 0', padding: '2px 0 4px', border: 'none', background: 'none',
      fontFamily: 'var(--ec-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.4, color: 'var(--ec-label)', cursor: 'pointer',
    }}
    >
      <span>&darr;</span>
      <span>{`Tap “Place order with ${methodName}” below`}</span>
      <span>&darr;</span>
    </button>
  );
}

// Brand-"classic" CTA treatment for PayPal/Venmo/Apple Pay — background/
// text/mark colors and sizes read live off the design's own per-method
// button. Google Pay is excluded: it renders its own real button
// (googleButtonRef below); Google's brand terms forbid redrawing it.
const CLASSIC_CTA = {
  paypal: {
    background: '#FFD070', color: '#3A363B', border: 'none', mark: payPalMark, markW: 96, markH: 28,
  },
  venmo: {
    background: '#1976D2', color: '#FFFFFF', border: 'none', mark: venmoCtaMark, markW: 88, markH: 22,
  },
  applepay: {
    background: '#FFFFFF', color: '#000000', border: '1px solid #000', mark: applePayCtaMark, markW: 70, markH: 28,
  },
};

function ClassicCta({ method, onClick, disabled, busyLabel }) {
  const c = CLASSIC_CTA[method];
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      width: '100%', minHeight: 56, height: 65, padding: '0 20px', border: c.border, borderRadius: 10,
      background: c.background, color: c.color, fontFamily: 'var(--ec-font)', fontWeight: 500,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'opacity 0.15s ease',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    }}
    >
      {busyLabel ? (
        <span style={{ fontSize: 15, letterSpacing: '0.02em', lineHeight: 1.3 }}>{busyLabel}</span>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, height: 28 }}>
          <span style={{ fontSize: 15, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            Place order with
          </span>
          <img src={c.mark} alt="" style={{ display: 'block', height: c.markH, width: c.markW, objectFit: 'contain' }} />
        </span>
      )}
    </button>
  );
}

function money(n) { return `$${Number(n).toFixed(2)}`; }

// "Curated around a Fall 2027 wedding" — season and year of the wedding date,
// same split as Steps 1-2: Jan-Feb Winter, Mar-May Spring, Jun-Aug Summer,
// Sep-Oct Fall, Nov-Dec Fall / Winter.
function themeLine(dt) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(dt || '');
  if (!m) return 'Curated around the wedding';
  const month = Number(m[2]);
  let season = 'Fall / Winter';
  if (month <= 2) season = 'Winter';
  else if (month <= 5) season = 'Spring';
  else if (month <= 8) season = 'Summer';
  else if (month <= 10) season = 'Fall';
  return `Curated around a ${season} ${m[1]} wedding`;
}

// Read aloud, never seen: the design keeps one live region for the messages
// that appear under a field, so a screen reader hears them even though the
// visible line is inserted already filled.
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap', border: 0,
};

// "Give way instead of wrapping" pieces of the order summary (STEP 4 handoff
// note). Nothing here uses a media query: each piece measures its own row.
const PROBE = {
  position: 'absolute', left: -9999, top: 0, visibility: 'hidden', whiteSpace: 'nowrap', pointerEvents: 'none', fontSize: 15,
};

// ✓ line that gives way instead of wrapping: an optional trailing qualifier
// ("($100+ value)") is dropped, or a shorter wording ("Monthly closer to the big
// day") is used, as soon as the full line wouldn't fit on ONE row — column width
// less the tick and its 7px gap. Measured against a hidden unwrapped copy, never
// the live line, so the choice can't flip back and forth.
function Benefit({ text, tail, short }) {
  const probeText = `${text}${tail || ''}`;
  const fit = useFit((row, probe) => {
    const tick = row.firstElementChild;
    return probe.offsetWidth > row.clientWidth - (tick ? tick.getBoundingClientRect().width : 0) - 7;
  });
  const shown = fit.tight ? (short || text) : probeText;
  return (
    <div ref={fit.rowRef} style={{ position: 'relative', display: 'flex', gap: 7, alignItems: 'flex-start' }}>
      <span style={{ flex: '0 0 auto', color: 'var(--ec-text)' }}>&#10003;</span>
      <span style={{ fontSize: 15, fontWeight: 400 }}>{shown}</span>
      {(tail || short) && <span ref={fit.probeRef} aria-hidden="true" style={PROBE}>{probeText}</span>}
    </div>
  );
}

// First Box (design): an optional 64px box picture, then the title with its
// promo pill and the delivery line, and the price on the right. The price column
// gives up its width first, stacking the struck price under the price; the
// picture is dropped when picture + text + the stacked price don't all fit. Both
// are decided from an off-screen copy at natural widths, so the answer doesn't
// depend on the layout it is about to change.
function FirstBox({
  promoPct, first, regular, deliveryDate,
}) {
  const rowRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const probeRef = React.useRef(null);
  const [iconOff, setIconOff] = React.useState(true);
  const showStrike = regular > first;
  const check = React.useCallback(() => {
    const row = rowRef.current;
    const title = titleRef.current;
    const probe = probeRef.current;
    if (!row || !title || !probe || !row.isConnected || probe.children.length < 3) return;
    const [strike, big, delivery] = probe.children;
    const priceMin = Math.max(strike.offsetWidth, big.offsetWidth);
    const textCol = Math.max(title.offsetWidth, delivery.offsetWidth);
    setIconOff(64 + 14 + textCol + 14 + priceMin > row.clientWidth);
  }, []);
  React.useLayoutEffect(() => {
    check();
    const ro = typeof ResizeObserver !== 'undefined' && rowRef.current ? new ResizeObserver(check) : null;
    if (ro) ro.observe(rowRef.current);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
    return () => { if (ro) ro.disconnect(); };
  }, [check, first, regular, deliveryDate, promoPct]);
  return (
    <div ref={rowRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, fontSize: 15, color: 'var(--ec-text)' }}>
      <span ref={probeRef} aria-hidden="true" style={{ ...PROBE, display: 'inline-flex', gap: 8, visibility: 'hidden' }}>
        <span>{money(regular)}</span>
        <span style={{ fontWeight: 500 }}>{money(first)}</span>
        <span style={{ lineHeight: 1.45 }}>Estimated delivery {deliveryDate}</span>
      </span>
      {!iconOff && <img src={firstBoxIcon} width={450} height={429} alt="" style={{ display: 'block', flex: '0 0 auto', width: 64, height: 64, objectFit: 'contain' }} />}
      <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          flex: '0 999 auto', minWidth: 'min-content', alignSelf: 'flex-start', display: 'flex', flexDirection: 'row-reverse', flexWrap: 'wrap',
          justifyContent: 'flex-start', alignItems: 'baseline', gap: '0 8px', textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 1.35,
        }}
        >
          <span style={{ fontWeight: 500 }}>{money(first)}</span>
          {showStrike && <span style={{ fontSize: 15, color: 'var(--ec-label)', textDecoration: 'line-through' }}>{money(regular)}</span>}
        </div>
        <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div ref={titleRef} style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'nowrap', minWidth: 0 }}>
            <span style={{ fontWeight: 500, lineHeight: 1.35, whiteSpace: 'nowrap' }}>The First Box</span>
            {promoPct && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', background: '#FDD7C2', color: '#333939', fontSize: 13, fontWeight: 500, lineHeight: 1,
                letterSpacing: '0.06em', textTransform: 'uppercase', padding: '5px calc(9px - 0.06em) 4px 9px', borderRadius: 999, whiteSpace: 'nowrap',
              }}
              >
                {promoPct}% off
              </span>
            )}
          </div>
          <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 6px', fontSize: 15, lineHeight: 1.45, color: 'var(--ec-label)' }}>
            <span style={{ whiteSpace: 'nowrap' }}>Estimated delivery</span><span style={{ whiteSpace: 'nowrap' }}>{deliveryDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// "Deliver to:" is a run of nowrap groups. A "·" that would END a line (the
// next group wrapped below it) is visibility:hidden — never display:none, so
// hiding it can't change the wrap that triggered it. A group too long for the
// line on its own is clipped with an ellipsis instead of widening the card.
function DeliverTo({ groups, onEdit }) {
  const lineRef = React.useRef(null);
  const [hidden, setHidden] = React.useState([]);
  const measure = React.useCallback(() => {
    const line = lineRef.current;
    if (!line || !line.isConnected) return;
    const els = Array.from(line.querySelectorAll('[data-grp]'));
    const next = els.map((el, i) => (i < els.length - 1 && els[i + 1].offsetTop - el.offsetTop > 2));
    setHidden((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, []);
  React.useLayoutEffect(measure);
  React.useEffect(() => {
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && lineRef.current) ro.observe(lineRef.current);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    return () => { if (ro) ro.disconnect(); };
  }, [measure]);
  const grpStyle = { whiteSpace: 'nowrap', maxWidth: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div ref={lineRef} style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0 5px', fontSize: 15, lineHeight: 1.45, color: 'var(--ec-label)' }}>
      {groups.map((g, i) => (
        <span key={g.key} data-grp="" style={grpStyle}>
          {g.text}
          {g.sep && <span style={{ visibility: hidden[i] ? 'hidden' : 'visible' }}> &middot;</span>}
        </span>
      ))}
      <button type="button" onClick={onEdit} style={{ padding: 0, border: 'none', background: 'none', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', color: '#333939', textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap', cursor: 'pointer' }}>Edit</button>
    </div>
  );
}

// Peach bonus row (design). The BRIDE Sweatshirt drops "BONUS:" and gets an eye +
// "view" that opens its preview; the Name Change Kit keeps its "BONUS:" label.
// The struck value is the first thing to go when the row is short of room,
// measured against the unwrapped full row. `note` is an optional line under it.
function BonusRow({ title, value, viewable, onView, note }) {
  const fit = useFit((row, probe) => probe.offsetWidth > row.clientWidth);
  const label = viewable ? title : `BONUS: ${title}`;
  return (
    <div style={{ fontSize: 15, color: 'var(--ec-text)', margin: '0 -12px', padding: '10px 12px', borderRadius: 10, background: '#FDD7C2' }}>
      <div ref={fit.rowRef} style={{ position: 'relative', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14 }}>
        <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
          {viewable ? (
            <button type="button" onClick={onView} aria-haspopup="dialog" aria-label="Preview the BRIDE Sweatshirt" style={{
              display: 'block', flex: '0 1 auto', minWidth: 0, minHeight: 32, margin: '-4px 0', padding: '4px 0', border: 'none', background: 'none',
              fontFamily: 'inherit', fontSize: 15, lineHeight: 1.4, textAlign: 'left', color: '#333939', cursor: 'pointer',
            }}
            >
              <span style={{ fontWeight: 500 }}>{label}</span>
              <span style={{ display: 'inline-block', width: 8 }} />
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, verticalAlign: 'bottom', position: 'relative', top: -2,
                color: 'var(--ec-label)', fontWeight: 400, fontSize: 14, whiteSpace: 'nowrap',
              }}
              >
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ display: 'block', flex: '0 0 auto', transform: 'translateY(1px)' }}>
                  <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                </svg>
                <span style={{ textDecoration: 'underline', textDecorationColor: '#AFAFAF', textUnderlineOffset: 2 }}>view</span>
              </span>
            </button>
          ) : (
            <span style={{ minWidth: 0, fontWeight: 500, color: '#333939' }}>{label}</span>
          )}
        </div>
        <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'row-reverse', flexWrap: 'nowrap', whiteSpace: 'nowrap', alignItems: 'baseline', gap: '0 8px' }}>
          <span style={{ fontWeight: 500, color: '#333939' }}>FREE</span>
          {!fit.tight && <span style={{ color: '#4F4F4F', textDecoration: 'line-through' }}>{money(value)}</span>}
        </div>
        <span ref={fit.probeRef} aria-hidden="true" style={{ ...PROBE, display: 'inline-flex', gap: 14 }}>
          <span style={{ fontWeight: 500 }}>{label}{viewable ? ' view' : ''}</span>
          <span style={{ display: 'inline-flex', gap: 8 }}><span>{money(value)}</span><span style={{ fontWeight: 500 }}>FREE</span></span>
        </span>
      </div>
      {note && <div style={{ marginTop: 6, fontSize: 15, lineHeight: 1.45, color: 'var(--ec-label)' }}>{note}</div>}
    </div>
  );
}

// The BRIDE Sweatshirt preview (design): a photo, its name, the struck value
// beside FREE, two facts and a Close. Escape, the backdrop, the X and Close all
// dismiss it.
function SweatshirtDialog({ open, onClose, value }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(35, 31, 30, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
    >
      <button type="button" onClick={onClose} aria-label="Close" className="ec-size-x" style={{
        position: 'fixed', top: 12, right: 12, zIndex: 81, margin: 0, background: '#FFFFFF', boxShadow: '0 2px 8px rgba(35, 31, 30, 0.2)',
      }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
      </button>
      <div role="dialog" aria-modal="true" aria-labelledby="sweatshirt-title" onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 450, maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', overscrollBehavior: 'contain', borderRadius: 14,
        animation: 'ecRise 0.2s ease-out', color: 'var(--ec-text)',
      }}
      >
        <div style={{ background: '#FFFFFF', borderRadius: 14, padding: '20px 20px 24px', boxShadow: '0 18px 44px rgba(35, 31, 30, 0.28)' }}>
          <img src={brideSweatshirtPhoto} width={540} height={675} decoding="async"
            alt="Smiling bride-to-be wearing the white BRIDE Sweatshirt with tonal embroidered lettering" style={{
              display: 'block', width: 'min(100%, calc(100dvh - 290px))', minWidth: 160, height: 'auto', aspectRatio: '1 / 1', margin: '0 auto 18px',
              borderRadius: 10, objectFit: 'cover', objectPosition: '50% 35%', background: 'var(--ec-row-active)',
            }} />
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px 12px' }}>
            <div id="sweatshirt-title" style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.25 }}>BRIDE Sweatshirt</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap', fontSize: 15 }}>
              <span style={{ textDecoration: 'line-through', color: 'var(--ec-label)' }}>{money(value)}</span>
              <span style={{ fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Free</span>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 15, lineHeight: 1.5, color: 'var(--ec-label)' }}>
            <div style={{ display: 'flex', gap: 6 }}><span aria-hidden="true" style={{ flex: '0 0 auto' }}>·</span><span style={{ minWidth: 0, textWrap: 'pretty' }}>Embroidered “BRIDE” white-on-white design</span></div>
            <div style={{ display: 'flex', gap: 6 }}><span aria-hidden="true" style={{ flex: '0 0 auto' }}>·</span><span style={{ minWidth: 0, textWrap: 'pretty' }}>Soft 100% cotton with a brushed fleece interior</span></div>
          </div>
          <button type="button" onClick={onClose} className="ec-cta" style={{ display: 'block', width: '100%', minHeight: 52, marginTop: 20 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Rendered into the page only by a template that starts the PHP session
// recurly_subs_purchase_mtm's CASE 1 CSRF check reads (template-react-
// no-header.php) — same helper as mtm-react-app-v2/TTM's subscribe-v2.
function csrfToken() {
  const el = document.querySelector('input[name=csrf_token]');
  if (!el) console.warn('[mtm-react-app-v3] no csrf_token on the page — purchase will be rejected as CASE 1');
  return el ? el.value : '';
}
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function addBusinessDays(d, n) {
  const x = new Date(d.getTime());
  let left = n;
  while (left > 0) { x.setDate(x.getDate() + 1); const day = x.getDay(); if (day !== 0 && day !== 6) left -= 1; }
  return x;
}
function addCadence(d, cadence) {
  const x = new Date(d.getTime());
  if (cadence.days) x.setDate(x.getDate() + cadence.days);
  else x.setMonth(x.getMonth() + cadence.months);
  return x;
}

// "• text" rows — the design's own "How Pay In 4 Works" modal list, a
// genuinely different bullet glyph from Step 2's "· " rows and this same
// card's own "✓" checklist — each context uses its own real glyph, read off
// the live DOM rather than assumed to share one.
function BulletRow({ children }) {
  return (
    <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
      <span style={{ flex: '0 0 auto', color: 'var(--ec-label)' }}>&bull;</span>
      <span>{children}</span>
    </div>
  );
}

// The server refuses a second subscription within 24 hours unless the
// customer says they meant it (recurly_functions.php:1588-1616) — this
// dialog's own shape (pink "Confirm" pill, a dark primary button AND an
// outline "No, go back" secondary) was never actually checked against the
// design before; the generic single-button NoticeDialog this used to reuse
// was missing the pill and the second button entirely. Read live via
// simulate="duplicate" on the real render_preview.
function DuplicateSubDialog({ open, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="You already have a subscription" style={{
      position: 'fixed', inset: 0, zIndex: 65, background: 'rgba(35, 31, 30, 0.42)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
    >
      <div style={{ width: '100%', maxWidth: 450, background: '#FFFFFF', borderRadius: 14, padding: 26, boxShadow: '0 18px 44px rgba(35, 31, 30, 0.28)' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, height: 28, padding: '0 12px', borderRadius: 999,
          background: '#FFE5E6', color: '#333939', fontSize: 13, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}
        >
          Confirm
        </div>
        <div style={{ marginTop: 14, fontSize: 19, fontWeight: 500, lineHeight: 1.25 }}>You already have a subscription</div>
        <div style={{ marginTop: 8, fontSize: 15, lineHeight: 1.55, color: '#5F5F5F', fontWeight: 400 }}>
          Our records show an active subscription on this email from the last 24 hours. If you meant to start a second one, confirm below and we&rsquo;ll place it.
        </div>
        <div style={{ marginTop: 20, display: 'grid', gap: 10 }}>
          <button type="button" onClick={onConfirm} style={{
            width: '100%', minHeight: 56, border: 'none', borderRadius: 10, background: '#333333', color: '#FFFFFF',
            fontFamily: 'var(--ec-font)', fontSize: 18, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
          }}
          >
            Yes, subscribe again
          </button>
          <button type="button" onClick={onCancel} style={{
            width: '100%', minHeight: 56, border: '1.5px solid #D7D7D7', borderRadius: 10, background: '#FFFFFF', color: 'var(--ec-text)',
            fontFamily: 'var(--ec-font)', fontSize: 18, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
          }}
          >
            No, go back
          </button>
        </div>
      </div>
    </div>
  );
}

function Pay4InfoModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="How Pay In 4 Works" style={{
      position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(35, 31, 30, 0.42)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
    >
      <div style={{ width: '100%', maxWidth: 450, background: '#FFFFFF', borderRadius: 14, padding: 22, boxShadow: '0 12px 32px rgba(35, 31, 30, 0.22)' }}>
        <div style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.3, marginBottom: 10 }}>How Pay In 4 Works</div>
        <div style={{ display: 'grid', gap: 7, fontSize: 15, lineHeight: 1.5, color: 'var(--ec-label)' }}>
          <div style={{ color: 'var(--ec-text)', fontWeight: 500 }}>Interest-free, no pre-approval or credit check needed</div>
          <BulletRow>Pay &ldquo;Total due today&rdquo; &amp; the first box ships</BulletRow>
          <BulletRow>Renewal is then split into 4 weekly payments.</BulletRow>
          <BulletRow>Next box ships once all 4 payments are complete</BulletRow>
        </div>
        <button type="button" onClick={onClose} style={{
          marginTop: 18, width: '100%', minHeight: 50, border: '1px solid #D7D7D7', borderRadius: 10,
          background: '#FFFFFF', fontFamily: 'var(--ec-font)', fontSize: 17, color: 'var(--ec-text)', cursor: 'pointer',
        }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

export default function Step4Pay({ config, state, onBack, onEvent, onNext }) {
  const recurly = useRecurly();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const formRef = React.useRef(null);
  const ccNameRef = React.useRef(null);
  const ccFirstRef = React.useRef(null);
  const ccLastRef = React.useRef(null);
  // Name On Card is an uncontrolled input (defaultValue + ref, not value) —
  // nothing ever wrote to state.ccName, so the "has-value" class the label
  // float depends on was always false once the field lost focus: the label
  // fell back to its centered resting position right on top of whatever was
  // typed, instead of floating up like every other field with a value.
  const [ccNameFilled, setCcNameFilled] = React.useState(() => state.audience !== 'gift' && !!(state.first || state.last));
  const [tokenError, setTokenError] = React.useState('');
  const [errorFields, setErrorFields] = React.useState([]);
  const [cardBrand, setCardBrand] = React.useState('');
  const [firstSix, setFirstSix] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [placed, setPlaced] = React.useState(false);
  const [dupOpen, setDupOpen] = React.useState(false);
  const [actionTokenId, setActionTokenId] = React.useState('');
  const auth3d = React.useRef(null);
  const inFlight = React.useRef(false);
  // From the tap that opens a wallet sheet (Apple Pay, PayPal, Venmo) until it
  // sends a token back, is closed or fails. A second tap while it is open used to
  // call begin() again: WebKit refuses ("Page already has an active payment
  // session") and Recurly drops the first sheet's session, so that sheet could
  // not finish. The ref is the guard (it changes at once); the state only shows
  // the busy label. TTM's shared-ui does the same with its own `submitting`.
  const walletBusy = React.useRef(false);
  const [walletOpen, setWalletOpen] = React.useState(false);
  const endWalletSheet = React.useCallback(() => { walletBusy.current = false; setWalletOpen(false); }, []);
  const payErrorRef = React.useRef(null);
  const billingErrorRef = React.useRef(null);
  const rootRef = React.useRef(null);
  // Every error shown on this step is also recorded (utils/pay-errors.js), with
  // the attempt number and what the page knew at the time. The context lives in
  // a ref, refreshed each render, so the handlers below don't go stale.
  const attempts = React.useRef(0);
  const errorContext = React.useRef({});
  const lastSubmit = React.useRef(null);

  const [billingCountry, setBillingCountry] = React.useState(state.country || 'US');
  const [billingPostal, setBillingPostal] = React.useState('');
  const gift = state.audience === 'gift';
  useFieldLift(rootRef);

  // Billing address (design): a gift buyer always types it; a self buyer's card is
  // billed to the delivery address unless they choose "+ Enter new billing info".
  // Either way recurly.token() reads country + postal code from data-recurly
  // inputs, so the shipping pair is passed through as hidden ones.
  const [sameBilling, setSameBilling] = React.useState(true);
  const [billingPostalErr, setBillingPostalErr] = React.useState('');
  const [sweatshirtOpen, setSweatshirtOpen] = React.useState(false);
  const billingShown = gift || !sameBilling;
  const billingCa = billingCountry === 'CA';

  const clearFieldError = (key) => setErrorFields((f) => f.filter((k) => k !== key));

  // The validated code from state (URL, or a marketing cookie), not a second raw read of the URL.
  const promoCode = (state.appliedCoupon || '').toLowerCase();
  const renewalPrice = DEFAULT_RENEWAL_PRICE;
  const first = promoCode ? (PROMO_PRICES[promoCode] ?? renewalPrice) : renewalPrice;
  const promoPct = promoCode ? (promoCode.match(/(\d+)/) || [])[1] : '';
  const shipping = SHIPPING_PRICE;
  // Recurly's own tax and total once it has priced this address; the flat
  // estimate only fills in until then (ClickUp 869fcqyj1).
  const quote = useQuote({ planCode: state.planCode, coupon: state.appliedCoupon, country: state.country, postal: state.postal });
  const tax = quote ? quote.tax : estimatedTax(first, shipping);
  const total = quote ? quote.total : first + shipping + tax;
  const cadence = cadenceFromPlanCode(state.planCode);
  const delivery = React.useMemo(() => addBusinessDays(new Date(), 7), []);
  const renewalDate = React.useMemo(() => addCadence(new Date(), cadence), [cadence]);
  const bonusTitle = state.bonusCode ? BONUS_TITLES[state.bonusCode] : '';
  // Pay-in-4 splits a renewal into four weekly payments, which can't fit
  // inside a 2-week cycle — accelerated plans never offer it (verified live
  // via the design's own `pay4Allowed = ... && !this.plan().days`).
  const pay4Allowed = !cadence.days;
  const [pay4, setPay4] = React.useState(false);
  const pay4On = pay4Allowed && pay4;
  const [pay4InfoOpen, setPay4InfoOpen] = React.useState(false);

  // Renewal line under the total — read verbatim off the design's own
  // renderVals() (STEP 4 OF 4 file): three real variants, not one generic
  // line reused for every cadence. The instalments split the renewal plus
  // its shipping, so only tax is still additional on top of each payment.
  const perInstallment = (renewalPrice + shipping) / 4;
  const renewalDateText = `${MONTHS_SHORT[renewalDate.getMonth()]} ${renewalDate.getDate()}`;
  let renewalHead; let renewalTail;
  if (pay4On) {
    if (cadence.months === 3) {
      renewalHead = 'Renews in 4 payments';
      renewalTail = `of ${money(perInstallment)} + tax every 3 weeks`;
    } else {
      renewalHead = `Renews in 4 ${cadence.months === 2 ? 'bi-weekly' : 'weekly'} payments`;
      renewalTail = `of ${money(perInstallment)} + tax`;
    }
  } else if (cadence.months === 2 || cadence.months === 3) {
    renewalHead = `Every ${cadence.months} months renewal, next:`;
    renewalTail = `${renewalDateText} · ${money(renewalPrice)} + shipping/tax`;
  } else {
    renewalHead = 'Next renewal:';
    renewalTail = `${renewalDateText} · ${money(renewalPrice)}${cadence.days ? ' every 2 weeks' : ''} + shipping/tax`;
  }

  // Legal paragraph's "Promotional first-box price ... require(s) three boxes
  // minimum" clause is conditional on whether a promo/bonus actually applies.
  // California ship-to gets one more sentence spelling out the early-
  // termination fee — same condition that gates the Prop 65 banner below, so
  // the two can never disagree about where the order is going.
  const hasPromo = first < renewalPrice;
  const reqSubject = hasPromo
    ? `Promotional first-box price${bonusTitle ? ` and free ${bonusTitle}` : ''}`
    : bonusTitle ? `Free ${bonusTitle}` : '';
  const reqVerb = hasPromo && bonusTitle ? 'require' : 'requires';
  const isCaliforniaShip = (state.country || 'US') === 'US' && state.region === 'CA';
  const authText = `By placing your order, you authorize automatic recurring charges at the price and frequency shown on this page until your plan ends after the final themed box. ${reqSubject ? `${reqSubject} ${reqVerb} three boxes minimum. ` : ''}${isCaliforniaShip && reqSubject ? 'If you cancel before your third box, an early termination fee applies. ' : ''}Pause or cancel online anytime in My Account before the next renewal.`;

  // Autofill and paste arrive as one jump: masked by the shape of what came in
  // rather than by the Country select, so an autofilled Canadian code under a
  // still-US country isn't stripped of its letters. Blur holds it to the country.
  const onBillingPostal = (e) => {
    const raw = e.target.value;
    const bulk = Math.abs(raw.length - billingPostal.length) > 1;
    const ca = bulk ? /[A-Za-z]/.test(raw) : billingCa;
    setBillingPostal(restoreZipHyphenLeadingZero(raw, maskPostal(raw, ca), ca));
    setBillingPostalErr('');
  };
  // Missing and wrong are told apart; a dropped leading zero is restored first.
  const judgeBillingPostal = (value) => {
    const v = padZipLeadingZero(String(value || '').trim().replace(/-+$/, ''), billingCa);
    const ok = billingCa ? /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i.test(v) : /^\d{5}(-\d{4})?$/.test(v);
    const name = billingCa ? 'postal code' : 'ZIP code';
    let error = '';
    if (!v) error = `Enter ${name}`;
    else if (!ok) error = `Enter a valid ${name}`;
    return { value: v, error };
  };
  const onBillingPostalBlur = () => {
    const { value, error } = judgeBillingPostal(billingPostal);
    setBillingPostal(value);
    setBillingPostalErr(value ? error : '');
  };

  const syncCcName = (rawValue) => {
    const full = rawValue.trim();
    const at = full.lastIndexOf(' ');
    if (ccFirstRef.current) ccFirstRef.current.value = at === -1 ? full : full.slice(0, at);
    if (ccLastRef.current) ccLastRef.current.value = at === -1 ? full : full.slice(at + 1);
  };

  // Real submission to recurly_subs_purchase_mtm — field-for-field the
  // payload that PHP function reads (recurly_functions.php:1114 on). No
  // `dev` key: omitting it routes to the endpoint's live Recurly/Braintree
  // account, same as going to production.
  const submitPurchase = React.useCallback(async (extra) => {
    const payload = {
      csrf_token: csrfToken(),
      email: state.email,
      ship_fname: state.first,
      ship_lname: state.last,
      ship_street: state.street,
      ship_apt: state.apt || '',
      ship_city: state.city,
      ship_country: state.country || 'US',
      ship_state: state.region,
      ship_zip: state.postal,
      ship_phone: state.phone || '',
      plan: state.planCode,
      applied_code: state.appliedCoupon || '',
      addon: state.country === 'CA' ? 'ca-shipping' : 'us-shipping',
      // Pay-in-4 is a real Recurly add-on, not its own PHP field —
      // recurly_subs_purchase_mtm only ever reads `bonus` (comma-split into
      // one add-on per code, recurly_functions.php:1258-1267) and has no
      // "installment" key at all. mtm-react-app-v2's own step4-pay.jsx sends
      // a separate top-level `installment` field the same way this file used
      // to — that field is silently dropped server-side, so PAY-IN-4 has no
      // effect there either; folded into `bonus` here instead, matching how
      // TTM's shared-ui step4-pay.jsx (buildBonusString) actually gets this
      // working in production.
      //
      // bonusCode is the BARE code ("freebridesweatshirt"/"namechange") —
      // the real Recurly items are "promo-freebridesweatshirt"/"promo-
      // namechange" (confirmed off react-app's own fallbackAddOnList).
      // Sending the bare code is exactly what produced the live error
      // "Add-on is invalid; Unit amount can't be blank; Item must exist" —
      // Recurly has no item by that bare name. react-app's own checkout.page
      // .jsx prefixes every bonus code with "promo-" except installment ones
      // (never prefixed); mirrored here since this app has no live add-on
      // list to check against either.
      //
      // The applied coupon ALSO becomes its own addon — checkout.page.jsx's
      // own `addonCode = promo.toLowerCase().replace("off","")` (e.g.
      // "mtm50off" -> "mtm50" -> "promo-mtm50"), tracked as a minimum-box-
      // commitment line item alongside the real Recurly coupon redemption
      // (applied_code above). Without it the discounted price shows in the
      // UI but nothing on the order records what commitment that discount
      // came with.
      bonus: [
        state.bonusCode ? `promo-${state.bonusCode}` : '',
        state.appliedCoupon ? `promo-${state.appliedCoupon.toLowerCase().replace('off', '')}` : '',
        pay4 ? COMMERCE.installmentCode : '',
      ].filter(Boolean).join(','),
      wed_date: state.wedDate,
      gift: state.audience === 'gift' ? 'yes' : 'no',
      shirt: state.topSize || '',
      shoe: '',
      giftee_email: state.giftEmail || '',
      personal_msg: state.giftMessage || '',
      ...(lastSubmit.current || {}),
      ...(extra || {}),
    };
    lastSubmit.current = payload;
    const { data } = await axios.post('/api/recurly_subs_purchase_mtm', payload);
    return data;
  }, [state, pay4]);

  // Shows the error banner AND records it. `stage` is where it came from (see
  // utils/pay-errors.js); `extra` adds what only the caller knows.
  const showError = React.useCallback((stage, text, extra) => {
    setTokenError(text);
    const c = errorContext.current;
    reportPayError({
      stage,
      message: text,
      attempt: attempts.current,
      method: c.method,
      planCode: c.state && c.state.planCode,
      country: c.state && c.state.country,
      shipPostal: c.state && c.state.postal,
      email: c.state && c.state.email,
      billingCountry: c.billingCountry,
      billingPostal: c.billingPostal,
      ...extra,
    });
  }, []);

  const handleResult = (data, httpStatus) => {
    if (data && data.success) { setPlaced(true); return true; }
    if (data && data.action_token_id) { setActionTokenId(data.action_token_id); return false; }
    const msg = (data && data.errors && data.errors[0]) || '';
    if (msg.toLowerCase().includes('already have a subscription')) {
      setDupOpen(true);
      const c = errorContext.current;
      reportPayError({
        stage: 'duplicate_subscription', message: msg, serverMessage: msg, httpStatus, attempt: attempts.current, method: c.method,
        planCode: c.state && c.state.planCode, country: c.state && c.state.country, shipPostal: c.state && c.state.postal,
        email: c.state && c.state.email, billingCountry: c.billingCountry, billingPostal: c.billingPostal,
      });
      return false;
    }
    showError('server', msg || 'Your payment could not be completed. Please try again.', { serverMessage: msg, httpStatus });
    return false;
  };

  const submit = React.useCallback(async (extra) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setTokenError('');
    setSubmitting(true);
    try {
      let data;
      let httpStatus = 200;
      try {
        data = await submitPurchase(extra);
      } catch (e) {
        // The fraud-rejection path (CSRF/time-gate/recaptcha/prepaid-BIN/past
        // wed_date) is the ONLY one of recurly_subs_purchase_mtm's outcomes
        // that answers with a non-200 status — axios throws on that, but the
        // body still carries the same {success,errors[]} shape every other
        // outcome does, so it must be read from e.response.data rather than
        // treated as a network failure. Ported from TTM's subscribe-v2.page.jsx.
        data = (e.response && e.response.data) || null;
        httpStatus = e.response ? e.response.status : 0;
        if (!data) { showError('network', "We couldn't reach the server. Please try again.", { httpStatus }); return; }
      }
      const ok = handleResult(data, httpStatus);
      if (ok) {
        if (onEvent) onEvent('order-placed', { planCode: state.planCode, invoiceId: data.invoice_id });
        // Gift messages never reach Recurly or recurly_subs_purchase_mtm (it
        // doesn't read giftee_email/personal_msg) — production's own
        // validateAndSubmitSubs delivers them separately, via Brevo's
        // mtm_gift_message_trigger event, only after a successful purchase.
        // Ported field-for-field: sib_gift_msg reads {email, message}, and
        // "email" there is the recipient's address. The recipient email is
        // optional here, so when it's blank the message goes to the
        // purchaser's own address instead of being dropped.
        if (gift && (state.giftMessage || '').trim()) {
          // Awaited (not fire-and-forget) so the request isn't cancelled by
          // whatever navigation onComplete triggers next — same ordering as
          // production's sibGiftMsg(...).then(() => redirect).
          await axios.post('/api/sib_gift_msg', {
            email: state.giftEmail || state.email, message: state.giftMessage,
          }).catch(() => { /* best-effort — doesn't block order completion */ });
        }
        // Step4Pay has no `onComplete` prop at all — App.js's `shared` object
        // only hands down {config, state, patch, onEvent, onNext, onBack}.
        // 'pay' is the last entry in STEPS, so App.js's own `onNext` is what
        // actually calls the real top-level onComplete (and clears saved flow
        // state on success) — calling a prop this component was never given
        // meant "Order placed — taking you to your confirmation…" showed and
        // then genuinely nothing happened next.
        if (onNext) onNext({ ...data, price: Number(total).toFixed(2) });
        return;
      }
    } catch (e) {
      showError('exception', (e && e.message) || 'Something went wrong. Please try again.');
    }
    inFlight.current = false;
    setSubmitting(false);
  }, [submitPurchase, onEvent, onNext, state, gift, total, showError]);

  React.useEffect(() => {
    if (!actionTokenId) return undefined;
    const challenge = recurly.Risk().ThreeDSecure({ actionTokenId });
    challenge.on('token', (token) => { setActionTokenId(''); submit({ action_token_result: token.id }); });
    challenge.on('error', (err) => {
      setActionTokenId('');
      showError('three_d_secure', (err && err.message) || 'Your bank could not verify this card. Please try again.');
    });
    challenge.attach(auth3d.current);
    if (auth3d.current) auth3d.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return () => { try { challenge.remove(); } catch { /* already gone */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTokenId, recurly]);

  // A billing-address failure (the token refused only country / postal code, or
  // the order was turned down over the billing address) is told under the Billing
  // block; every other message sits above the pay button, where the customer is
  // looking after pressing it.
  const billingFail = !!tokenError && isBillingAddressFailure(tokenError, errorFields);
  React.useEffect(() => {
    if (!tokenError) return;
    const target = billingFail ? billingErrorRef.current : payErrorRef.current;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenError]);

  const placeOrder = () => {
    if (inFlight.current || submitting) return;
    // A separate billing address needs its ZIP / postal code; empty and malformed
    // get different messages (same rule as Step 3). Billed to the delivery
    // address, Step 3's own code is used instead.
    if (billingShown) {
      const { value, error } = judgeBillingPostal(billingPostal);
      setBillingPostal(value);
      setBillingPostalErr(error);
      if (error) {
        requestAnimationFrame(() => {
          const el = document.getElementById('billingPostal');
          if (!el) return;
          window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - 120), behavior: 'smooth' });
          el.focus({ preventScroll: true });
        });
        return;
      }
    }
    attempts.current += 1;
    if (!ccFirstRef.current?.value.trim() || !ccLastRef.current?.value.trim()) {
      syncCcName(ccNameRef.current?.value || '');
    }
    setTokenError('');
    setErrorFields([]);
    recurly.token(formRef.current, async (err, token) => {
      if (err) {
        const fields = err.fields || [];
        setErrorFields(fields);
        const named = [...new Set(fields.map((f) => FIELD_NAMES[f]).filter(Boolean))];
        showError('card_token', named.length ? `Please check: ${named.join(', ')}.` : (err.message || 'Please check your card details.'), { fields, serverMessage: err.message });
        return;
      }
      const recaptcha_value = executeRecaptcha ? await executeRecaptcha('purchase').catch(() => '') : '';
      submit({
        recurly_token: token.id, card_brand: cardBrand, first_six: firstSix, pay_source: 'creditcard', recaptcha_value,
      });
    });
  };

  const onWalletToken = React.useCallback((source, tokenId) => {
    endWalletSheet();
    attempts.current += 1;
    submit({ recurly_token: tokenId, pay_source: source });
  }, [submit, endWalletSheet]);
  const onWalletError = React.useCallback((source, err) => {
    endWalletSheet();
    // The customer closed the wallet sheet. Nothing went wrong, so it is not
    // logged as an error, but they are told so, and what to do next, in the same
    // box as every other message above Place order.
    if (!err) {
      setTokenError(`${WALLET_NAMES[source] || 'That payment method'} was canceled. Try again or choose another payment method.`);
      return;
    }
    // statusCode / statusMessage: what Google (or the wallet) said, when it said one.
    showError('wallet', (err && err.message) || 'Your payment could not be completed. Please try again.', {
      method: source, serverMessage: err.message, statusCode: err.statusCode, statusMessage: err.statusMessage,
    });
  }, [showError, endWalletSheet]);
  const wallets = useWallets({
    recurly, config: COMMERCE.wallets, total, onToken: onWalletToken, onError: onWalletError,
  });
  const [method, setMethod] = React.useState('card');
  errorContext.current = { method, state, billingCountry, billingPostal };
  const choose = (key) => { endWalletSheet(); setMethod(key); setTokenError(''); };
  const walletLabel = wallets.appleCapable ? 'Apple Pay' : 'Google Pay';
  const busyLabel = placed ? 'Order placed — taking you to your confirmation…'
    : submitting || walletOpen ? 'Processing…' : actionTokenId ? 'Verifying with your bank…' : '';
  // The "↓ Tap “Place order with X” below ↓" hint only POINTS at the button —
  // pressing it used to start PayPal/Venmo/Apple Pay itself. Same as the
  // design's scrollToPay: bring the button's centre two-thirds of the way down
  // the screen, so it's clearly in view with the order summary still above it.
  const payBtnRef = React.useRef(null);
  const scrollToPay = () => {
    const el = payBtnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    window.scrollBy({ top: r.top + r.height / 2 - (window.innerHeight * 2) / 3, behavior: 'smooth' });
  };
  const triggerMethod = () => {
    if (method === 'card') { placeOrder(); return; }
    // One wallet sheet at a time: a tap while one is open (or an order is going
    // through) is ignored, not passed on to begin().
    if (walletBusy.current || inFlight.current || submitting) return;
    const key = method === 'wallet' ? (wallets.appleCapable ? 'apple' : 'google') : method;
    walletBusy.current = true;
    setWalletOpen(true);
    if (!wallets.start(key)) {
      endWalletSheet();
      showError('wallet_not_ready', 'This payment method is not ready yet. Please try again in a moment.', { method: key });
    }
  };
  const [prop65Open, setProp65Open] = React.useState(false);

  return (
    <StepShell stepLabel={config.copy.step4Label || 'STEP 4 OF 4: REVIEW & PAY'} runway>
      <div ref={rootRef}>
      <Card title="Choose a payment method">
        <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
          {wallets.appleCapable && wallets.available.apple && (
            <PayRow selected={method === 'wallet'} label="Apple Pay" mark={applePayRowMark} onClick={() => choose('wallet')} />
          )}
          {!wallets.appleCapable && wallets.available.google && (
            <PayRow selected={method === 'wallet'} label="Google Pay" mark={googlePayRowMark} onClick={() => choose('wallet')} />
          )}
          {method === 'wallet' && <PayHint methodName={walletLabel} onClick={scrollToPay} />}

          {wallets.available.paypal && (
            <PayRow selected={method === 'paypal'} label="PayPal" mark={payPalMark} onClick={() => choose('paypal')} />
          )}
          {method === 'paypal' && <PayHint methodName="PayPal" onClick={scrollToPay} />}

          {/* US-only, same as checkout.page.jsx's own
              `venmoAvailable && supportCountries === "US"` gate — Venmo
              itself isn't restricted this way, but this is the real
              checkout's own deliberate choice, not a technical limit. */}
          {wallets.available.venmo && (state.country || 'US') === 'US' && (
            <PayRow selected={method === 'venmo'} label="Venmo" mark={venmoRowMark} onClick={() => choose('venmo')} />
          )}
          {method === 'venmo' && (state.country || 'US') === 'US' && <PayHint methodName="Venmo" onClick={scrollToPay} />}

          {/* Card row — always mounted, since Recurly's hosted fields live
              inside; just visually collapsed to a plain unselected row when
              another method is chosen. */}
          <div style={{
            minWidth: 0, borderRadius: 10, overflow: 'visible',
            background: method === 'card' ? '#FAFAFA' : '#FFFFFF',
            border: `1.5px solid ${method === 'card' ? 'var(--ec-border-open)' : 'var(--ec-border)'}`,
            transition: 'border-color 0.18s, background 0.18s',
          }}
          >
            <button type="button" onClick={() => choose('card')} style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 60, padding: '12px 16px',
              border: 'none', background: 'none', fontFamily: 'var(--ec-font)', color: 'var(--ec-text)', cursor: 'pointer',
            }}
            >
              <span style={{
                flex: '0 0 auto', boxSizing: 'border-box', width: 22, height: 22, borderRadius: 999, transition: '0.18s',
                border: method === 'card' ? '6px solid var(--ec-border-open)' : '1.5px solid #AFAFAF', background: '#FFFFFF',
              }}
              />
              {/* Never wraps (STEP 4 handoff note): the label holds one line and
                  ellipsizes, and the logos shrink together — each keeps width
                  38px + aspect-ratio 38/25 as it shrinks, and "+4" stretches
                  to the logo height instead of having a fixed one of its own. */}
              <span style={{
                flex: '1 1 auto', minWidth: 0, fontSize: 15, fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              >
                Credit/Debit Card
              </span>
              <span style={{ display: 'flex', alignItems: 'center', flex: '0 1 auto', minWidth: 0, gap: 4 }}>
                {[cardVisaMark, cardMasterMark, cardAmexMark].map((src, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 1 auto', minWidth: 0, width: 38, height: 'auto', aspectRatio: '38 / 25',
                  }}
                  >
                    <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: 4 }} />
                  </span>
                ))}
                <MoreCardsButton />
              </span>
            </button>
            <div style={{ display: method === 'card' ? 'block' : 'none', padding: 16, borderTop: '1px solid var(--ec-row-active)' }}>
              <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
                <div style={{ display: 'grid', gap: 12, animation: 'ecRise 0.22s ease-out' }}>
                  <HostedField label="Card Number" invalid={errorFields.includes('number')} icon={<CardNumberIcon />}
                    onChange={(s) => { setCardBrand(s.brand || ''); setFirstSix(s.firstSix || ''); clearFieldError('number'); }}>
                    <CardNumberElement style={ELEMENT_STYLE} />
                  </HostedField>
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--ec-label)' }}>
                      Card expiration
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <HostedField label="Month (MM)" invalid={errorFields.includes('month')}
                        onChange={() => { clearFieldError('month'); }}>
                        <CardMonthElement style={ELEMENT_STYLE} />
                      </HostedField>
                      <HostedField label="Year (YY)" invalid={errorFields.includes('year')}
                        onChange={() => { clearFieldError('year'); }}>
                        <CardYearElement style={ELEMENT_STYLE} />
                      </HostedField>
                    </div>
                  </div>
                  <HostedField label="Security Code (CVV)" invalid={errorFields.includes('cvv')} icon={<CvvIcon />}
                    onChange={() => { clearFieldError('cvv'); }}>
                    <CardCvvElement style={ELEMENT_STYLE} />
                  </HostedField>

                  <div className={`ec-field ec-field--plain ${ccNameFilled ? 'has-value' : ''}`}>
                    <input id="ccName" name="ccName" className="ec-field-control" ref={ccNameRef} autoComplete="cc-name"
                      defaultValue={gift ? '' : [state.first, state.last].filter(Boolean).join(' ')}
                      onChange={(e) => { syncCcName(e.target.value); setCcNameFilled(!!e.target.value.trim()); }} />
                    <span className="ec-field-label">Name On Card</span>
                  </div>
                  <input ref={ccFirstRef} type="hidden" data-recurly="first_name" defaultValue={state.first || ''} />
                  <input ref={ccLastRef} type="hidden" data-recurly="last_name" defaultValue={state.last || ''} />

                  {/* "Your card details stay private" trust row — read
                      verbatim off the design (STEP 4 OF 4 file). */}
                  <div style={{ marginTop: -2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 500, lineHeight: 1.35, color: 'var(--ec-text)' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
                        <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" stroke="#231F1E" strokeWidth="1.7" />
                        <path d="M8.2 10.5V7.6a3.8 3.8 0 0 1 7.6 0v2.9" stroke="#231F1E" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>
                      Your card details stay private
                    </div>
                    <div style={{ marginTop: 4, fontSize: 14, lineHeight: 1.2, color: 'var(--ec-label)' }}>
                      Card information is encrypted. Miss To Mrs staff never have access to your full card number.
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--ec-row-active)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--ec-label)' }}>
                      Billing address
                    </div>
                    {!gift && (
                      <button type="button" onClick={() => setSameBilling((v) => !v)} style={{
                        flex: '0 0 auto', background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 14,
                        color: '#333939', textDecoration: 'underline', textUnderlineOffset: 4, cursor: 'pointer',
                      }}
                      >
                        {sameBilling ? '+ Enter new billing info' : '+ Use shipping address'}
                      </button>
                    )}
                  </div>
                  {!billingShown && (
                    <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.4, color: 'var(--ec-text)' }}>
                      {[state.street, state.apt].filter(Boolean).join(', ')}
                      <br />
                      {[[state.city, state.region].filter(Boolean).join(', '), state.postal].filter(Boolean).join(' ')}
                      {', '}
                      {state.country === 'CA' ? 'Canada' : 'United States'}
                    </div>
                  )}
                </div>

                {/* Select is a styled button + popup, not a real form control —
                    recurly.token() scans the form for elements carrying
                    data-recurly, so hidden inputs are the only way these values
                    reach Recurly's tokenizer. */}
                {!billingShown && (
                  <>
                    <input type="hidden" data-recurly="country" value={state.country || 'US'} readOnly />
                    <input type="hidden" data-recurly="postal_code" value={state.postal || ''} readOnly />
                  </>
                )}
                {billingShown && (
                  <>
                    <div style={{ marginTop: 8, fontSize: 15, lineHeight: 1.45, color: 'var(--ec-label)' }}>
                      Enter billing info linked to your card.
                    </div>
                    <div style={{ position: 'relative', marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'ecRise 0.22s ease-out' }}>
                      <Select id="billingCountry" label="Country" options={countryOptions()} value={billingCountry} clearable={false}
                        onChange={(e) => { setBillingCountry(e.target.value); setBillingPostal(''); setBillingPostalErr(''); }} />
                      <input type="hidden" data-recurly="country" value={billingCountry} readOnly />
                      <div style={{ display: 'grid', gap: 6, minWidth: 0, alignContent: 'start' }}>
                        <div className={`ec-field ${billingPostal ? 'has-value' : ''} ${billingPostalErr ? 'has-error' : ''}`}>
                          <input id="billingPostal" name="billingPostal" className="ec-field-control" data-recurly="postal_code" type="text"
                            inputMode={billingCa ? 'text' : 'numeric'} pattern={billingCa ? undefined : '[0-9]*'} autoCapitalize={billingCa ? 'characters' : 'none'}
                            autoCorrect="off" spellCheck={false} autoComplete="billing postal-code" placeholder=" " value={billingPostal}
                            onChange={onBillingPostal} onBlur={onBillingPostalBlur}
                            aria-invalid={billingPostalErr ? 'true' : 'false'} aria-describedby="ec-err-billing-postal" />
                          <span className="ec-field-label">{billingCa ? 'Postal Code' : 'ZIP Code'}</span>
                        </div>
                        {billingPostalErr && (
                          <span id="ec-err-billing-postal" style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 15, lineHeight: 1.4, color: '#A51F20' }}>
                            <span>{billingPostalErr}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
                <div role="alert" style={SR_ONLY}>{billingPostalErr}</div>
                {billingFail && method === 'card' && <div ref={billingErrorRef}><Alert>{billingFailureText(tokenError, errorFields)}</Alert></div>}
              </form>
            </div>
          </div>
        </div>
      </Card>

      {/* Order summary (design): a dark plan band across the top, the theme line
          and ticks under it, then the First Box, shipping + where it goes, the
          bonus, tax and the total. Every line that could wrap gives way instead
          (see Benefit / FirstBox / BonusRow). */}
      <div style={{
        margin: '20px 0 0', background: '#FAFAFA', border: '1px solid var(--ec-border)', borderRadius: 14, overflow: 'hidden', padding: '0 clamp(20px, 5vw, 28px) 14px',
      }}
      >
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px 16px', margin: '0 calc(-1 * clamp(20px, 5vw, 28px))', padding: '12px 20px', background: '#333939', color: '#FFFFFF',
        }}
        >
          <div style={{ fontSize: 17, fontWeight: 500, letterSpacing: '0.02em', lineHeight: 1.2 }}>{`${cadence.name.toUpperCase()} PLAN`}</div>
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.35, fontWeight: 500, color: 'var(--ec-text)', marginTop: 14, marginBottom: 12, textWrap: 'pretty' }}>
          {themeLine(state.wedDate)}
        </div>
        <div style={{ display: 'grid', gap: 2, fontSize: 15, lineHeight: 1.4, color: 'var(--ec-text)', marginBottom: 14 }}>
          <Benefit text="6–8 full-size products in every box" tail=" ($100+ value)" />
          {cadence.months >= 2 && <Benefit text="Switches to monthly closer to the big day" short="Monthly closer to the big day" />}
          <Benefit text="Automatically ends after the final theme" />
          <Benefit text="Pause or cancel anytime" />
        </div>

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ec-border)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 22 }}>
          <FirstBox
            promoPct={promoPct}
            first={first}
            regular={renewalPrice}
            deliveryDate={`${MONTHS_SHORT[delivery.getMonth()]} ${delivery.getDate()}`}
          />

          <div style={{ fontSize: 15, color: 'var(--ec-text)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14 }}>
              <span style={{ flex: '1 1 auto', minWidth: 0, fontWeight: 500 }}>Flat-rate Shipping</span>
              <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{money(shipping)}</span>
            </div>
            {state.country === 'CA' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6, fontSize: 15, lineHeight: 1.45, color: 'var(--ec-label)' }}>
                <img src={flagCaMark} alt="Canada" style={{ flex: '0 0 auto', width: 20, height: 14, display: 'block', objectFit: 'contain', borderRadius: 2 }} />
                <span style={{ minWidth: 0, color: 'var(--ec-label)' }}>Ships from within Canada &middot; No extra fees</span>
              </div>
            )}
            <DeliverTo
              onEdit={onBack}
              groups={[
                { key: 'lead', text: 'Deliver to:', sep: false },
                { key: 'name', text: [state.first, state.last].filter(Boolean).join(' '), sep: true },
                { key: 'street', text: [state.street, state.apt].filter(Boolean).join(' · '), sep: true },
                { key: 'city', text: [state.city, state.region, state.postal].filter(Boolean).join(' · '), sep: true },
                { key: 'country', text: state.country === 'CA' ? 'Canada' : 'United States', sep: false },
              ]}
            />
          </div>

          {bonusTitle && (
            <BonusRow
              title={bonusTitle}
              value={BONUS_VALUES[state.bonusCode] || 40}
              viewable={state.bonusCode === 'freebridesweatshirt'}
              onView={() => setSweatshirtOpen(true)}
              note={state.sweatshirtBlocked ? 'BRIDE Sweatshirt only applies to plans 5+ months away from wedding day.'
                : state.bonusCode === 'freebridesweatshirt' ? 'Ships with the first box.' : ''}
            />
          )}
        </div>

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ec-border)', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14, fontSize: 15, color: 'var(--ec-text)' }}>
          <span style={{ flex: '1 1 auto', minWidth: 0, lineHeight: 1.4, fontWeight: 500 }}>Estimated Sales Tax</span>
          <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{money(tax)}</span>
        </div>

        <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14 }}>
          <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 18, fontWeight: 500, lineHeight: 1.4 }}>Total due today (USD)</span>
          <span style={{ fontSize: 18, fontWeight: 500, whiteSpace: 'nowrap' }}>{money(total)}</span>
        </div>
        <div style={{ marginTop: 6, fontSize: 15, lineHeight: 1.45, color: 'var(--ec-text)', textWrap: 'pretty' }}>
          {renewalHead} <span style={{ whiteSpace: 'nowrap' }}>{renewalTail}</span>
        </div>

        {pay4Allowed && (
        <button type="button" onClick={() => setPay4((v) => !v)} style={{
          marginTop: 16, padding: 0, border: 'none', background: 'none', width: '100%', display: 'flex', alignItems: 'flex-start', gap: 14,
          fontFamily: 'var(--ec-font)', color: 'var(--ec-text)', cursor: 'pointer', textAlign: 'left',
        }}
        >
          <span style={{
            flex: '0 0 auto', marginTop: 5, width: 52, height: 30, borderRadius: 999, display: 'inline-flex', alignItems: 'center',
            padding: 3, transition: 'background 0.18s', background: pay4 ? 'var(--ec-peach)' : '#D7D7D7',
          }}
          >
            <span style={{
              width: 24, height: 24, borderRadius: 999, background: '#FFFFFF', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', transition: 'transform 0.18s', transform: pay4 ? 'translateX(22px)' : 'translateX(0)',
            }}
            >
              {pay4 && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ display: 'block' }}>
                  <path d="M3 7.4L5.8 10L11 4.2" stroke="#231F1E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
          </span>
          <span style={{ flex: '1 1 auto', minWidth: 0 }}>
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 500, lineHeight: 1.35 }}>
              <svg width="24" height="8" viewBox="0 0 24 8" fill="none" style={{ flex: '0 0 auto' }}>
                <rect x="0.5" y="0.5" width="4.4" height="7" rx="1.2" fill="#333939" />
                <rect x="6.9" y="0.5" width="4.4" height="7" rx="1.2" fill="#AFAFAF" />
                <rect x="13.3" y="0.5" width="4.4" height="7" rx="1.2" fill="#AFAFAF" />
                <rect x="19.1" y="0.5" width="4.4" height="7" rx="1.2" fill="#AFAFAF" />
              </svg>
              <span style={{ whiteSpace: 'nowrap', fontSize: 14 }}>PAY-IN-4</span>
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setPay4InfoOpen(true); }} style={{ whiteSpace: 'nowrap', fontWeight: 400, color: '#333939', textDecoration: 'underline', textUnderlineOffset: 2, position: 'relative', top: -1, cursor: 'pointer' }}>
                How it works
              </span>
            </span>
            <span style={{ display: 'block', marginTop: 0, fontWeight: 400, lineHeight: 1.4, color: 'var(--ec-text)', fontSize: 14 }}>Pay renewals in 4 instalments</span>
          </span>
        </button>
        )}
      </div>

      {isCaliforniaShip && (
        <div style={{ marginTop: 20, borderRadius: 10, padding: '14px 20px', background: '#F2F2F2' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, lineHeight: 1.45, color: '#636566' }}>
            <span style={{ flex: '0 0 auto', fontSize: 15 }} aria-hidden="true">&#9888;</span>
            <span style={{ color: '#333333', fontSize: 15 }}>Proposition 65 Warning (California Only)</span>
            <button type="button" aria-label="Proposition 65 Warning" onClick={() => setProp65Open(true)} style={{
              flex: '0 0 auto', width: 20, height: 20, borderRadius: 999, border: '1px solid #AFAFAF',
              background: 'none', fontFamily: 'var(--ec-font)', fontSize: 12, lineHeight: 1, color: '#636566', cursor: 'pointer',
            }}
            >
              i
            </button>
          </span>
        </div>
      )}

      {prop65Open && (
        <div role="dialog" aria-modal="true" aria-label="Proposition 65 Warning" style={{
          position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(35, 31, 30, 0.42)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{ width: '100%', maxWidth: 450, background: '#FFFFFF', borderRadius: 14, padding: 22, boxShadow: '0 12px 32px rgba(35, 31, 30, 0.22)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 19, fontWeight: 500, lineHeight: 1.3, marginBottom: 10 }}>
              <span style={{ flex: '0 0 auto' }} aria-hidden="true">&#9888;</span>
              Proposition 65 Warning
            </div>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--ec-label)' }}>
              Some products included in your subscription box may contain chemicals known to the State of California to
              cause cancer, birth defects, or other reproductive harm. For more information, visit{' '}
              <a href="https://www.P65Warnings.ca.gov" target="_blank" rel="noreferrer" style={{ color: '#333939' }}>
                www.P65Warnings.ca.gov
              </a>
            </p>
            <button type="button" onClick={() => setProp65Open(false)} style={{
              marginTop: 18, width: '100%', minHeight: 56, border: '1px solid #D7D7D7', borderRadius: 10,
              background: '#FFFFFF', fontFamily: 'var(--ec-font)', fontSize: 17, fontWeight: 500, color: 'var(--ec-text)', cursor: 'pointer',
            }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <Pay4InfoModal open={pay4InfoOpen} onClose={() => setPay4InfoOpen(false)} />
      <SweatshirtDialog open={sweatshirtOpen} onClose={() => setSweatshirtOpen(false)} value={BONUS_VALUES.freebridesweatshirt || 40} />

      {/* The authorisation sits in a light-grey panel in the design (10px radius,
          18px 20px padding, #F2F2F2, 15px / 1.3 #333) — not bare text on the
          page background. */}
      <div style={{ marginTop: 20, borderRadius: 10, padding: '18px 20px', backgroundColor: '#F2F2F2' }}>
        <p style={{ margin: 0, color: '#333333', fontStyle: 'normal', fontSize: 15, lineHeight: 1.3 }}>{authText}</p>
      </div>

      {tokenError && !(billingFail && method === 'card') && <div ref={payErrorRef}><Alert>{tokenError}</Alert></div>}

      {actionTokenId && <div ref={auth3d} style={{ margin: '16px 0' }} />}

      <div ref={payBtnRef} style={{ marginTop: 22 }}>
      {method === 'card' ? (
        <button type="button" className="ec-cta ec-cta--pay" onClick={placeOrder} disabled={!!busyLabel}
          style={placed ? { fontSize: 15, letterSpacing: '0.02em', textTransform: 'none', lineHeight: 1.3 } : undefined}>
          {busyLabel || 'Place order'}
        </button>
      ) : method === 'wallet' && !wallets.appleCapable && !busyLabel ? (
        <div ref={wallets.googleButtonRef} className="ec-gpay" style={{ width: '100%', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!wallets.available.google && <span style={{ fontSize: 13, color: 'var(--ec-label)' }}>Loading Google Pay…</span>}
        </div>
      ) : method === 'wallet' && !wallets.appleCapable ? (
        <Button text={busyLabel} disabled />
      ) : (
        <ClassicCta method={method === 'wallet' ? 'applepay' : method} onClick={triggerMethod} disabled={!!busyLabel} busyLabel={busyLabel} />
      )}
      </div>
      {/* Two separate icon+phrase pills, not one small plain-text line — read
          live off the design (STEP 4 OF 4 file): 15px/weight-500 text, a real
          lock icon (same one "Your card details stay private" uses) before
          "Secure Payment", a plain checkmark before "Pause or cancel
          anytime", wrapped with its own row/column gaps. */}
      <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 18px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 15, lineHeight: 1.4, color: 'var(--ec-text)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto', width: 16 }}>
            <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" stroke="#231F1E" strokeWidth="1.7" />
            <path d="M8.2 10.5V7.6a3.8 3.8 0 0 1 7.6 0v2.9" stroke="#231F1E" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 15, fontWeight: 500 }}>Secure Payment</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 15, lineHeight: 1.4, color: 'var(--ec-text)' }}>
          <span aria-hidden="true">&#10003;</span>
          <span style={{ fontSize: 15, fontWeight: 500 }}>Pause or cancel anytime</span>
        </span>
      </div>
      <div style={{ textAlign: 'center', fontSize: 14, lineHeight: 1.45, color: 'var(--ec-label)', marginTop: 14 }}>
        This site is protected by reCAPTCHA.
      </div>

      <LookingFor step={4} gift={gift} style={{ marginTop: 20 }} />

      <DuplicateSubDialog
        open={dupOpen}
        onConfirm={async () => {
          setDupOpen(false);
          const extra = { duplicate_confirmed: true };
          // recurly_subs_purchase_mtm re-checks recaptcha_value from scratch on
          // every request when pay_source is "creditcard" — reusing the first
          // attempt's token here would fail as already-consumed (reCAPTCHA v3
          // tokens are single-use), so a card purchase needs a fresh one.
          if ((lastSubmit.current || {}).pay_source === 'creditcard' && executeRecaptcha) {
            extra.recaptcha_value = await executeRecaptcha('purchase').catch(() => '');
          }
          submit(extra);
        }}
        onCancel={() => setDupOpen(false)}
      />
      </div>
    </StepShell>
  );
}
